import React, { useState, useEffect } from "react";
import MobileReservationModal from "../components/MobileReservationModal";
import MobileSearchModal from "../components/MobileSearchModal";
import FloorplanOverlay from "../components/FloorplanOverlay";
import Calendar from "react-calendar";

const MobileDashboard = ({ user, onLogout }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [reservations, setReservations] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewReservation, setShowNewReservation] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showFloorplan, setShowFloorplan] = useState(false);

  const todayStr = new Date().toLocaleDateString("en-CA");
  const selectedStr = selectedDate.toLocaleDateString("en-CA");
  const isToday = selectedStr === todayStr;

  const dateLabel = isToday
    ? "Danas"
    : selectedDate.toLocaleDateString("hr-HR", { day: "numeric", month: "long", year: "numeric" });

  const [stats, setStats] = useState({
    total_reservations: 0,
    total_guests: 0,
    free_tables: {
      morning_indoor: 0, morning_outdoor: 0,
      afternoon_indoor: 0, afternoon_outdoor: 0,
      evening_indoor: 0, evening_outdoor: 0,
    }
  });

  useEffect(() => {
    fetch("/reservations/api/shifts/")
      .then(res => res.json())
      .then(data => setShifts(data.shifts || []));

    fetch("/reservations/api/tables/")
      .then(res => res.json())
      .then(data => setTables(data.tables || []));
  }, []);

	useEffect(() => {
		fetchReservations();

		fetch(`/reservations/api/reservations/stats/?date=${selectedStr}`)
			.then(res => res.json())
			.then(data => setStats(data))
			.catch(() => {});
	}, [selectedDate]);

  useEffect(() => {
    fetchReservations();
  }, [selectedDate]);

  const fetchReservations = () => {
    setLoading(true);
    fetch(`/reservations/api/reservations/by-date/?date=${selectedStr}`)
      .then(res => res.json())
      .then(data => {
        const sorted = (data.reservations || []).sort((a, b) => {
          if (a.start_datetime && b.start_datetime)
            return a.start_datetime.localeCompare(b.start_datetime);
          return 0;
        });
        setReservations(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getPeriodColor = (time) => {
    if (!time) return "bg-gray-700";
    const [h] = time.split(":").map(Number);
    if (h >= 8 && h < 12) return "bg-blue-800";
    if (h >= 12 && h < 20) return "bg-yellow-700";
    return "bg-red-900";
  };

  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const isPast = (r) => {
    if (!isToday || !r.start_datetime) return false;
    const [h, m] = r.start_datetime.split(":").map(Number);
    return (h * 60 + m) < nowMinutes;
  };

  const upcoming = reservations.filter(r => !isPast(r));
  const past = reservations.filter(r => isPast(r));
  const allSorted = [...upcoming, ...past];

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">

      {/* Header */}
      <div className="border-b border-[#c1b428]">
        <div className="bg-black flex items-center justify-center py-2 px-4" style={{ height: "54px" }}>
          <img 
            src="/pub_cover.jpg" 
            alt="The House Pub" 
            style={{ maxHeight: "50px", maxWidth: "100%", objectFit: "contain" }}
          />
        </div>
        <div className="bg-gray-900 px-4 py-1.5 flex justify-between items-center">
          <span className="text-[#c1b428] text-xs">👤 {user?.username} <span className="text-gray-400">({user?.role})</span></span>
          <button onClick={onLogout} className="text-red-400 text-xs hover:text-red-300">
            Odjava
          </button>
        </div>
      </div>

      {/* Datum picker */}
      <div className="px-4 py-3 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 text-[#c1b428] font-bold text-lg"
          >
            📅 {dateLabel}
            <span className="text-xs text-gray-400">{showDatePicker ? "▲" : "▼"}</span>
          </button>
          {!isToday && (
            <button
              onClick={() => { setSelectedDate(new Date()); setShowDatePicker(false); }}
              className="text-xs bg-gray-700 text-[#c1b428] px-2 py-1 rounded hover:bg-gray-600"
            >
              Danas
            </button>
          )}
        </div>

        {showDatePicker && (
          <div className="mt-2">
            <Calendar
              onChange={(date) => {
                setSelectedDate(date);
                setShowDatePicker(false); // ✅ automatski zatvori
              }}
              value={selectedDate}
              className="react-calendar text-[#c1b428] bg-gray-800 border-none w-full"
              locale="hr-HR"
            />
          </div>
        )}

        <div className="mt-1 text-xs text-gray-400">
          {reservations.length} rezervacija
        </div>
      </div>

			{/* Statistika */}
			<div className="px-4 py-2 bg-gray-900 border-b border-gray-700">
				<div className="grid grid-cols-2 gap-2 text-xs mb-2">
					<div className="bg-gray-800 rounded-lg p-2 text-center">
						<div className="text-[#c1b428] font-bold text-lg">{stats.total_reservations}</div>
						<div className="text-gray-400">Rezervacija</div>
					</div>
					<div className="bg-gray-800 rounded-lg p-2 text-center">
						<div className="text-[#c1b428] font-bold text-lg">{stats.total_guests}</div>
						<div className="text-gray-400">Gostiju</div>
					</div>
				</div>

				<div className="bg-gray-800 rounded-lg p-2 text-xs">
					<div className="flex justify-between items-center mb-1">
						<span className="text-gray-400">Slobodnih stolova:</span>
						<span className="flex gap-3 text-[#c1b428] font-semibold">
							<span>🏠</span><span>🌿</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">☀️ Jutro:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.morning_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.morning_outdoor}</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">🌤️ Poslijepodne:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.afternoon_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.afternoon_outdoor}</span>
						</span>
					</div>
					<div className="flex justify-between items-center">
						<span className="text-gray-400">🌙 Večer:</span>
						<span className="flex gap-3 font-semibold">
							<span className="w-5 text-center">{stats.free_tables.evening_indoor}</span>
							<span className="w-5 text-center">{stats.free_tables.evening_outdoor}</span>
						</span>
					</div>
				</div>
			</div>

      {/* Akcijski gumbi */}
      <div className="px-4 py-3 flex gap-2">
        {["moderator", "admin"].includes(user?.role) && (
          <button
            onClick={() => setShowNewReservation(true)}
            className="flex-1 bg-[#c1b428] text-black font-bold rounded-lg py-3 text-sm"
          >
            + Nova rezervacija
          </button>
        )}
        <button
          onClick={() => setShowSearch(true)}
          className="flex-1 bg-gray-800 text-[#c1b428] border border-[#c1b428] font-semibold rounded-lg py-3 text-sm"
        >
          🔍 Pretraga
        </button>
        <button
          onClick={() => setShowFloorplan(true)}
          className="flex-1 bg-gray-800 text-[#c1b428] border border-[#c1b428] font-semibold rounded-lg py-3 text-sm"
        >
          🗺️ Stolovi
        </button>
      </div>

      {/* Lista rezervacija */}
      <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-2">
        {loading ? (
          <p className="text-center text-gray-400 py-8">Učitavam...</p>
        ) : allSorted.length > 0 ? (
          allSorted.map((r) => {
            const past = isPast(r);
            return (
              <div
                key={r.id}
                className={`rounded-xl border p-3 transition-opacity ${
                  past
                    ? "bg-gray-900 border-gray-700 opacity-40"
                    : "bg-gray-900 border-gray-700"
                }`}
              >
                {/* Gornji red – stol, termin */}
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-black font-bold px-2 py-0.5 rounded text-xs ${
                    past ? "bg-gray-500" : "bg-[#c1b428]"
                  }`}>
                    {r.tables.join(", ")}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded text-white ${getPeriodColor(r.start_datetime)}`}>
                    {r.start_datetime} – {r.end_datetime}
                  </span>
                </div>

                {/* Donji red – ime, broj gostiju */}
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm">{r.guest_name}</span>
                  <span className="text-gray-400 text-xs">👥 {r.guests}</span>
                </div>

                {r.note && (
                  <div className="mt-1 text-xs text-gray-400 italic">📝 {r.note}</div>
                )}

                {r.shift && (
                  <div className="mt-0.5 text-xs text-gray-500">{r.shift}</div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">Nema rezervacija za odabrani datum.</p>
          </div>
        )}
      </div>

      {/* Modali */}
      {showNewReservation && (
        <MobileReservationModal
          tables={tables}
          shifts={shifts}
          selectedDate={selectedDate}
          onClose={() => setShowNewReservation(false)}
          onSuccess={() => {
            setShowNewReservation(false);
            fetchReservations();
          }}
        />
      )}

      {showSearch && (
        <MobileSearchModal
          onClose={() => setShowSearch(false)}
        />
      )}

      {showFloorplan && (
        <FloorplanOverlay
          onClose={() => setShowFloorplan(false)}
          selectedDate={selectedDate}
          shifts={shifts}
          user={user}
        />
      )}

    </div>
  );
};

export default MobileDashboard;